import { es, enUS } from 'date-fns/locale'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuItem, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import Dialog from '../common/dialog'
import { FormError } from '../../types'
import i18n from '../../utils/i18n.utils'
import { Filter } from '../../containers/new-insights-management'
import CancelCloseIcon from '../../assets/icons/cancel-close-icon.svg'
import { textFieldDarkGreenBg, textFieldWhiteBg } from '../../theme/custom-styles'
import { format } from 'date-fns'

export const Filters = ({
  dataFilter,
  setDataFilter,
  handleApplyFilterToData,
  handleApplyCustomFilterToData
}: {
  dataFilter: Filter
  setDataFilter: Dispatch<SetStateAction<Filter>>
  handleApplyFilterToData: (filterApplied: Filter) => void
  handleApplyCustomFilterToData: (fromDateToFilter: Date, toDateToFilter: Date) => void
}) => {
  const { t } = useTranslation(['insights-funding-history-management', 'customer-details-management', 'reports-management'])

  const [errors, setErrors] = useState<FormError>({})
  const [toDateToFilter, setToDateToFilter] = useState<Date | null>(null)
  const [openCustomFilterModal, setOpenCustomFilterModal] = useState(false)
  const [customFilterApplied, setCustomFilterApplied] = useState<string>('')
  const [fromDateToFilter, setFromDateToFilter] = useState<Date | null>(null)

  useEffect(() => {
    if (dataFilter !== Filter.CUSTOM) {
      handleApplyFilterToData(dataFilter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilter])

  const handleFilterAndDispayReport = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}

    if (dataFilter === Filter.CUSTOM && !fromDateToFilter) {
      formDataIsValid = false
      formErrors.fromDateToFilter = t('modals.paymentsHistory.errors.fromDateToFilter', { ns: 'reports-management' })
    }

    if (dataFilter === Filter.CUSTOM && !toDateToFilter) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilter', { ns: 'reports-management' })
    }

    if (dataFilter === Filter.CUSTOM && fromDateToFilter && toDateToFilter && toDateToFilter?.getTime() < fromDateToFilter?.getTime()) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilterEarlier', { ns: 'reports-management' })
    }

    if (!formDataIsValid) {
      setErrors(formErrors)
      return
    }

    setOpenCustomFilterModal(false)
    setCustomFilterApplied(t('insights.filters.selectedCustomFilter', { fromDate: format(new Date(fromDateToFilter as Date), 'dd/MM/yyyy'), toDate: format(new Date(toDateToFilter as Date), 'dd/MM/yyyy') }))
    handleApplyCustomFilterToData(fromDateToFilter as Date, toDateToFilter as Date)
  }

  return (
    <>
      <div className="flex-custom-position flex min-h-14 w-full items-center gap-2 sm:gap-3 md:gap-8 text-xs sm:text-sm md:text-base flex-wrap mt-4 md:mt-8 max-md:max-w-full">
        <div
          className="text-[#107569] font-semibold my-auto"
          style={{
            width: '235px',
            height: '24px',
            fontFamily: 'Open Sans',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: '0%'
          }}
        >
          {t('insights.filters.subtitle')}
        </div>
        <div className="flex-grow">
          <TextField
            select
            fullWidth
            variant="standard"
            label={t('insights.range')}
            sx={{ ...textFieldDarkGreenBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            value={dataFilter}
            onChange={(event) => {
              const selectedValue = event.target.value as unknown as Filter
              setDataFilter(selectedValue)

              if (selectedValue === Filter.CUSTOM) {
                setOpenCustomFilterModal(true)
              }
            }}
          >
            <MenuItem disabled>{t('insights.filters.select')}</MenuItem>
            <MenuItem value={Filter.LAST_TWO_HOURS}>{t('insights.filters.lastTwoHours')}</MenuItem>
            <MenuItem value={Filter.LAST_DAY}>{t('insights.filters.lastDay')}</MenuItem>
            <MenuItem value={Filter.LAST_WEEK}>{t('insights.filters.lastWeek')}</MenuItem>
            <MenuItem value={Filter.LAST_MONTH}>{t('insights.filters.lastMonth')}</MenuItem>
            <MenuItem value={Filter.CUSTOM}>{t('insights.filters.custom')}</MenuItem>
          </TextField>
        </div>
      </div>
      {customFilterApplied && (
        <div style={{ margin: '15px 0px' }}>
          <div className="filter-chip-container">
            <div>{customFilterApplied}</div>
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setCustomFilterApplied('')
                setDataFilter(Filter.LAST_DAY)
              }}
            >
              <img
                src={CancelCloseIcon}
                alt="cancel-close-icon"
                style={{ width: '24px', height: '24px' }}
              />
            </div>
          </div>
        </div>
      )}
      {openCustomFilterModal && (
        <Dialog
          dialogTitle={t('insights.filters.modal.title')}
          handleDialogAction={handleFilterAndDispayReport}
          handleCloseOptionAction={() => {
            setDataFilter(Filter.LAST_DAY)
            setToDateToFilter(null)
            setFromDateToFilter(null)
            setOpenCustomFilterModal(false)
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setDataFilter(Filter.LAST_DAY)
              setToDateToFilter(null)
              setFromDateToFilter(null)
            }

            setOpenCustomFilterModal(openState)
          }}
          actionButtonText={t('actionsBar.filter', { ns: 'customer-details-management' })}
        >
          <div
            style={{ alignItems: 'flex-end', gap: '20px' }}
            className="payments-history-modal-full-column-container"
          >
            <div className="payments-history-modal-options-column">
              <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.selectSettlementDateRange', { ns: 'reports-management' })}</div>
              <div>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
                >
                  <DatePicker
                    label={t('modals.paymentsHistory.options.fromDate', { ns: 'reports-management' })}
                    sx={{ width: '100%' }}
                    onChange={(newValue) => setFromDateToFilter(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        error: !!errors.fromDateToFilter,
                        helperText: errors.fromDateToFilter,
                        sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="payments-history-modal-options-column">
              <div>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
                >
                  <DatePicker
                    label={t('modals.paymentsHistory.options.toDate', { ns: 'reports-management' })}
                    sx={{ width: '100%' }}
                    onChange={(newValue) => setToDateToFilter(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        error: !!errors.toDateToFilter,
                        helperText: errors.toDateToFilter,
                        sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}
